import { IBreadcrumbItem, IStyle } from '@fluentui/react';
import { Button, buttonStylesPrimary, IPageHeaderStyles, mediaMobile, PageHeader, useClassNames } from '@h2oai/ui-kit';
import { useHistory, useLocation } from 'react-router-dom';

import { ClassNamesFromIStyles } from '../../utils/models';

interface IBreadcrumbsStyles {
  root: IStyle;
  actionButtonContainer: IStyle;
  actionButton: IStyle;
}

const breadcrumbsStyles: Partial<IBreadcrumbsStyles> = {
  root: {
    display: 'flex',
    flexDirection: 'row',
    // Adds extra margin to prevent "Need help?" button from overlapping.
    margin: '0px 10px',
  },
  actionButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'end',
    alignItems: 'end',
    ...mediaMobile({
      position: 'fixed',
      bottom: '8vh',
      right: '5vw',
      width: '60vw',
      height: 60,
      zIndex: 10,
    }),
  },
  actionButton: {
    marginRight: 10,
    marginBottom: 5,
    ...mediaMobile({
      width: '60vw',
      height: 60,
      '.ms-Button-label': {
        fontSize: 30,
      },
      '.ms-Icon': {
        fontSize: 30,
      },
      '.ms-Button-textContainer ': {
        display: 'flex',
        justifyContent: 'center',
      },
    }),
  },
};

const headerStyles: IPageHeaderStyles = {
  root: {
    padding: '10px 20px',
    minWidth: 500,
  },
};

export type HeaderPureProps = {
  actionButtonProps?: {
    disabled?: boolean;
    icon?: string;
    onClick: () => void;
    title: string;
  };
  items: IBreadcrumbItem[];
  pageTitle: string;
};

export const HeaderPure = (props: HeaderPureProps) => {
  const { actionButtonProps } = props;
  const classNames = useClassNames<IBreadcrumbsStyles, ClassNamesFromIStyles<IBreadcrumbsStyles>>(
    'breadcrumbs',
    breadcrumbsStyles
  );

  return (
    <div className={classNames.root}>
      <PageHeader
        breadcrumbProps={{
          items: props.items,
          styles: {
            itemLink: {
              textTransform: 'capitalize',
            },
          },
        }}
        styles={headerStyles}
        pageTitle={props.pageTitle}
      />
      {actionButtonProps ? (
        <div className={classNames.actionButtonContainer}>
          <Button
            className={classNames.actionButton}
            styles={buttonStylesPrimary}
            iconProps={actionButtonProps.icon ? { iconName: actionButtonProps.icon } : undefined}
            text={actionButtonProps.title}
            onClick={actionButtonProps.onClick}
            disabled={actionButtonProps.disabled}
          />
        </div>
      ) : undefined}
    </div>
  );
};

export type HeaderProps = {
  actionTitle?: string;
  onActionClick?: () => void;
  customPageTitle?: string;
  actionIcon?: string;
  isActionButtonDisabled?: boolean;
};

const Header = ({ actionTitle, onActionClick, actionIcon, isActionButtonDisabled, customPageTitle }: HeaderProps) => {
  const history = useHistory(),
    location = useLocation(),
    path = location.pathname.split('/'),
    pageTitle = path[path.length - 1],
    items = ['home', ...path]
      .filter((it) => it !== '')
      .map((item, idx) => {
        return {
          key: item,
          // Regex to add space between camel case words.
          text: item === 'create-new' ? 'Create new' : item.replace(/([a-z])([A-Z])/g, '$1 $2'),
          onClick: () => history.push(path.slice(0, idx + 1).join('/') || '/'),
        };
      });

  return (
    <HeaderPure
      items={items}
      pageTitle={customPageTitle || pageTitle}
      actionButtonProps={
        !actionTitle
          ? undefined
          : {
              disabled: isActionButtonDisabled,
              icon: actionIcon,
              onClick: onActionClick || (() => {}),
              title: actionTitle,
            }
      }
    />
  );
};

export default Header;
